import { isMobile } from "@common_lib/base/utils/src/tools"
import setting from "@source/setting.json"

const customSettings = import.meta.glob(["@source/labels/**/*.scss"], {
  query: "?inline"
})

const templateCssStyle = import.meta.glob("@source/themes/**/*.css", {
  query: "?inline"
})

const faviconVariants = import.meta.glob("@source/labels/**/favicon.ico", {
  query: "?inline"
})

const isDevelopment = import.meta.env.VITE_DOMAIN_CHANGE === "true"

const getFileName = (websiteName: string) => {
  const uiCSSFileName = websiteName.toLowerCase()
  const forceCSSFileName = isDevelopment && localStorage.getItem("website")
  const fileName = forceCSSFileName || uiCSSFileName

  return fileName
}

const getForceTheme = () => {
  const forceCSSThemeName = isDevelopment ? localStorage.getItem("theme") : null

  return forceCSSThemeName
}

const appendChildInHead = (module: { default: string }, id: string) => {
  const style = document.createElement("style")
  style.id = id
  style.textContent = module.default

  document.head.appendChild(style)
}

const loadCustomSettingWithScss = async (websiteName: string) => {
  try {
    const deviceType = isMobile() ? "mobile" : "desktop"
    const fallbackDeviceType = deviceType === "mobile" ? "desktop" : "mobile"
    const fileName = getFileName(websiteName)
    const targetSetting = `/submodules/9wicket_client_source/labels/${fileName}/${deviceType}/setting.scss`
    const defaultSetting = `/submodules/9wicket_client_source/labels/${fileName}/${fallbackDeviceType}/setting.scss`

    const module = customSettings[targetSetting] || customSettings[defaultSetting]

    if (!module) throw new Error("Invalid setting")

    const renderModule = (await module()) as { default: string }
    appendChildInHead(renderModule, "")
  } catch (error) {
    console.error("Failed to import custom setting:", error)
  }
}

const applyCssStyle = async (websiteName: string) => {
  try {
    const deviceType = isMobile() ? "mobile" : "desktop"
    const fileName = getFileName(websiteName)

    const webSiteSetting = setting[fileName as keyof typeof setting]
    if (typeof webSiteSetting === "string") throw new Error("Invalid setting")

    const forceDSName = getForceTheme()
    const themes = forceDSName || webSiteSetting?.themes[deviceType]

    if (themes === undefined) throw new Error("Invalid template")

    const [[, module] = [undefined, undefined]] = Object.entries(templateCssStyle).filter(
      ([path]) => path.includes(deviceType) && path.split("/").pop() === `${themes}.css`
    )
    if (module === undefined) throw new Error("Invalid setting themes")

    const loadModule = (await module()) as { default: string }
    appendChildInHead(loadModule, "custom-theme")
  } catch (error) {
    console.error("Failed to import custom style css:", error)
  }
}

const setFaviconHref = async (websiteName: string) => {
  try {
    const deviceType = isMobile() ? "mobile" : "desktop"
    const fallbackDeviceType = deviceType === "mobile" ? "desktop" : "mobile"
    const fileName = getFileName(websiteName)
    const targetFavicon = `/submodules/9wicket_client_source/labels/${fileName}/${deviceType}/images/favicon.ico`
    const defaultFavicon = `/submodules/9wicket_client_source/labels/${fileName}/${fallbackDeviceType}/images/favicon.ico`

    const module = faviconVariants[targetFavicon] || faviconVariants[defaultFavicon]

    // 取得或建立 favicon link 元素
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement

    if (!module) {
      // 當找不到 favicon 時，移除 link 元素以避免瀏覽器載入預設 favicon
      // 例如：UAT 的 API 白牌都是使用同一個網址，如果不移除會導致 favicon 抓到前一次開啟的白牌的 favicon
      if (link) link.remove()
      throw new Error("Invalid faviconURL")
    }

    if (!link) {
      link = document.createElement("link")
      link.rel = "icon"
      document.head.appendChild(link)
    }

    const renderModule = await module()
    link.href = (renderModule as { default: string }).default
  } catch (e) {
    console.error("Failed to create favicon URL:", e)
  }
}

const loadCustomSetting = async (websiteName: string) => {
  await applyCssStyle(websiteName)
  await loadCustomSettingWithScss(websiteName)
  await setFaviconHref(websiteName)
}

export default loadCustomSetting
